import { computed, ref } from 'vue'
import { IconCompany, IconEdit, type IconComponent } from '@gat-ship/icons'
import type { Company } from '@gat-ship/types'
import { globalUtils } from '@gat-ship/utils'
import { useForm, z } from '@gat-ship/vue-components'
import { useCompanyCollectionContext } from './useCompanyCollection'

type CompanyFormMode = 'add' | 'edit'

const companyFormSchema = z.object({
  code: z.string().min(1),
  name: z.string().min(1, 'Name is required').max(120, 'Max 120 characters'),
  description: z.string().max(500, 'Max 500 characters').optional()
})
type CompanyFormValues = z.infer<typeof companyFormSchema>

/**
 * View-model for the unified add/edit company modal. Owns the form, the current
 * mode, and the mode-derived presentation (title, icon, header description,
 * submit label, dirty requirement). `showAdd` / `showEdit` seed the form and
 * open the modal; submit dispatches to the matching collection command.
 *
 * One form instance reconfigured per show — replacing the former duplicate
 * CompanyModalAdd / CompanyModalEdit pair.
 */
export function useCompanyFormModal() {
  const collection = useCompanyCollectionContext()!

  const isOpen = ref(false)
  const mode = ref<CompanyFormMode>('add')
  // The edited company's name, shown as the edit header description.
  const editingName = ref('')

  const form = useForm({
    defaultValues: {
      code: '',
      name: '',
      description: ''
    } as CompanyFormValues,
    validators: {
      onChange: companyFormSchema
    },
    onSubmit: async ({ value }) => {
      const payload = {
        name: value.name,
        description: value.description ?? undefined
      }
      if (mode.value === 'add') {
        await collection.commands.add({ code: value.code, ...payload })
      } else {
        await collection.commands.changeDetails(value.code, payload)
      }
      isOpen.value = false
    }
  })

  function showAdd() {
    mode.value = 'add'
    editingName.value = ''
    form.reset({
      code: globalUtils.functions.uuid().substring(0, 20),
      name: '',
      description: ''
    })
    isOpen.value = true
  }

  function showEdit(company: Company) {
    mode.value = 'edit'
    editingName.value = company.name
    form.reset({
      code: company.code,
      name: company.name,
      description: company.description ?? ''
    })
    isOpen.value = true
  }

  const isAdd = computed(() => mode.value === 'add')
  const title = computed(() => (isAdd.value ? 'Add Company' : 'Edit Company'))
  const icon = computed<IconComponent>(() =>
    isAdd.value ? IconCompany : IconEdit
  )
  const headerDescription = computed(() =>
    isAdd.value
      ? 'Add a company registered as a legal entity to your agency business.'
      : editingName.value
  )
  const submitLabel = computed(() =>
    isAdd.value ? 'Add Company' : 'Save Changes'
  )
  // Add can submit immediately; edit requires a change first.
  const requireDirty = computed(() => !isAdd.value)

  return {
    isOpen,
    form,
    title,
    icon,
    headerDescription,
    submitLabel,
    requireDirty,
    showAdd,
    showEdit
  }
}
